import React, { useEffect, useState } from "react";
import "./styles.css";
import { Container, Table } from "react-bootstrap";
import { BLANK_STRING, VIEW_MODES } from "../../constants/appConstants";
import SwitchViewData from "../../Components/SwitchViewData/SwitchViewData";
import MultiSelector from "../../Components/MultiSelector/MultiSelector";
import { useNavigate, useParams } from "react-router-dom";
import { exportQuarterAndYear, findCurrentQuarter, getDifferenceDays, getQuarterString, getQuarterYearObject } from "../../utils/commonUtils";
import { getPurchaseAcceptanceDataAPI } from "../../apiRequests/purchase-acceptance/getPurchaseAcceptanceDataAPI";
import { updateGRNDocumentStatusAPI } from "../../apiRequests/purchase-acceptance/updateGRNDocumentStatusAPI";
import MaskLoading from "../../Components/LoadingPage/MaskLoading";
import { getPurchaseAcceptanceDataByYearAPI } from "../../apiRequests/purchase-acceptance/getPurchaseAcceptanceDataByYearAPI";

interface PurchaseAcceptanceProps {
    isDashboard?: boolean;
    dataPAs?: any;
}

const PurchaseAcceptance = (props: PurchaseAcceptanceProps) => {
    const navigate = useNavigate();
    const { period } = useParams();
    const [dataPAs, setDataPAs] = useState<any>([]);
    const [currentViewMode, setCurrentViewMode] = useState<string>(VIEW_MODES.DETAILED);
    const [selectedYear, setSelectedYear] = useState<string>(BLANK_STRING);
    const [isRenderSelectQuarter, setIsRenderSelectQuarter] = useState<boolean>(false);
    const [selectedQuarter, setSelectedQuarter] = useState<string>(BLANK_STRING);
    const [selectedYearAndQuarter, setSelectedYearAndQuarter] = useState<string[]>([]);
    const [dataKPIForMultiQuarter, setDataKPIForMultiQuarter] = useState<any[]>([]);
    const [isUpdateDocumentStatus, setIsUpdateDocumentStatus] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    useEffect(() => {
        const getAllPAsData = async () => {
            if (currentViewMode === VIEW_MODES.TABLE && !dataPAs.every((data: any) => Boolean(data.isLoaded))) {
                setIsLoading(true);
                try {
                    const dataPAs = await getPurchaseAcceptanceDataAPI();
                    setDataPAs(dataPAs);
                } catch (error) {
                    console.error("Error fetching Purchase Acceptance data:", error);
                }
                setIsLoading(false);
            }
        };
        getAllPAsData();
    }, [currentViewMode]);
    useEffect(() => {
        !props.isDashboard && getPADataAndSetToState();
    }, []);

    useEffect(() => {
        if (props.isDashboard) {
            setDataPAs(props.dataPAs);
            setCurrentYearAndQuarter(props.dataPAs);
        }
    }, [props.dataPAs, props.isDashboard]);

    const setCurrentYearAndQuarter = (dataPAs: any) => {
        const { year, quarter } = exportQuarterAndYear(period || BLANK_STRING);
        const currentYear = new Date().getFullYear();
        if (year && quarter) {
            const currentPA = dataPAs.find((data: any) => data.yearGRNs.title.includes(year));
            setSelectedYear(currentPA?.yearGRNs?.id || BLANK_STRING);
            const currentQuarter = findCurrentQuarter(currentPA?.quarterGRNs, year, quarter);

            setSelectedQuarter(currentQuarter || BLANK_STRING);
        } else {
            const currentPA = dataPAs.find((data: any) => data.yearGRNs.title.includes(currentYear));
            setSelectedYear(currentPA?.yearGRNs?.id);
            const currentQuarter = Math.floor(new Date().getMonth() / 3) + 1;
            const selectedQuarter = findCurrentQuarter(currentPA?.quarterGRNs, currentYear, currentQuarter);
            setSelectedQuarter(selectedQuarter || BLANK_STRING);
            const quarterString = getQuarterString(currentQuarter);
            setSelectedYearAndQuarter([`GRN Q${currentQuarter},${currentYear}`]);
            !props.isDashboard && navigate(`/purchase-acceptance/${quarterString}-${currentYear}`, { replace: true });
        }
    };

    useEffect(() => {
        if (selectedYear) {
            setIsRenderSelectQuarter(true);
        }
    }, [selectedYear]);

    useEffect(() => {
        const quarterData = dataPAs.reduce((acc: any[], item: any) => {
            const selectedQuarters = Object.keys(item.quarterGRNs)
                .filter((quarterKey: string) => selectedYearAndQuarter.includes(quarterKey))
                .map((quarterKey: string) => ({ [quarterKey]: item.quarterGRNs[quarterKey] }));
            const sortedSelectedQuarters = selectedQuarters.sort((a, b) => {
                const keyA = Object.keys(a)[0];
                const keyB = Object.keys(b)[0];
                return sortStringQuarterAndYear(keyA, keyB);
            });
            return [...acc, ...sortedSelectedQuarters];
        }, []);

        if (quarterData) {
            const calculatedData = quarterData.map((data: any, index: number) => {
                const { KPI, passed, total } = calculateMetrics(data[selectedYearAndQuarter[index]]);
                return { KPI, passed, total };
            });
            setDataKPIForMultiQuarter(calculatedData);
        }
    }, [selectedYearAndQuarter, dataPAs]);

    const getPADataAndSetToState = async () => {
        setIsLoading(true);

        const { year: periodYear } = exportQuarterAndYear(period || BLANK_STRING);
        const targetYear = periodYear || new Date().getFullYear();

        try {
            const dataPAs = await getPurchaseAcceptanceDataByYearAPI(targetYear);
            setDataPAs(dataPAs);

            if (dataPAs.length && !isUpdateDocumentStatus) {
                setCurrentYearAndQuarter(dataPAs);
            }
        } catch (error) {
            console.error("Error fetching Purchase Acceptance data:", error);
        } finally {
            setIsUpdateDocumentStatus(false);
            setIsLoading(false);
        }
    };

    const handleYearChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
        const yearId = event.target.value;
        let selectedYearData = dataPAs.find((data: any) => data.yearGRNs.id === yearId);

        if (!selectedYearData) {
            console.error("Selected year data not found");
            return;
        }

        if (!Boolean(selectedYearData.isLoaded)) {
            selectedYearData = await handleLoadPAsDataForOneYear(yearId, selectedYearData);
        }
        const listQuarters = Object.keys(selectedYearData.quarterGRNs);
        setSelectedQuarter(listQuarters[listQuarters.length - 1]);
        setSelectedYear(yearId);
        const { quarter, year } = getQuarterYearObject(listQuarters[listQuarters.length - 1]);
        const quarterInString = getQuarterString(quarter);
        !props.isDashboard && navigate(`/purchase-acceptance/${quarterInString}-${year}`, { replace: true });
    };

    const handleLoadPAsDataForOneYear = async (yearId: string, selectedYearData: any) => {
        setIsLoading(true);
        try {
            const year = parseInt(selectedYearData.yearGRNs.title, 10);
            const result = await getPurchaseAcceptanceDataByYearAPI(year);

            const updatedDataPAs = mergePAsDataSets(dataPAs, result);
            selectedYearData = {
                ...selectedYearData,
                quarterGRNs: updatedDataPAs.find((data: any) => data.yearGRNs.id === yearId)?.quarterGRNs || {},
            };
            setDataPAs(updatedDataPAs);

            return selectedYearData;
        } catch (error) {
            console.error("Failed to fetch purchase acceptance data", error);
        } finally {
            setIsLoading(false);
        }
    };

    function mergePAsDataSets(dataSet1: any, dataSet2: any) {
        const mergedData = [...dataSet1];

        dataSet2.forEach((item2: any) => {
            const existingItem = mergedData.find((item1) => item1.yearGRNs.id === item2.yearGRNs.id);
            if (existingItem) {
                Object.keys(item2.quarterGRNs).forEach((quarter) => {
                    if (existingItem.quarterGRNs[quarter]) {
                        existingItem.quarterGRNs[quarter] = [
                            ...existingItem.quarterGRNs[quarter],
                            ...item2.quarterGRNs[quarter].filter(
                                (grn2: any) => !existingItem.quarterGRNs[quarter].some((grn1: any) => grn1.id === grn2.id)
                            ),
                        ];
                    } else {
                        existingItem.quarterGRNs[quarter] = item2.quarterGRNs[quarter];
                    }
                });
            } else {
                mergedData.push(item2);
            }
        });

        return mergedData;
    }

    const renderSelectPAsYear = () => {
        return (
            <div className="selector-container">
                <label className="label" htmlFor="year">
                    Select Year:
                </label>
                <select className="select" id="year" value={selectedYear} onChange={(event) => handleYearChange(event)}>
                    <option disabled value="">
                        Select Year
                    </option>
                    {dataPAs.map((data: any) => (
                        <option key={data.yearGRNs.title} value={data.yearGRNs.id}>
                            {data.yearGRNs.title}
                        </option>
                    ))}
                </select>
            </div>
        );
    };

    const handleQuarterChange = async (event: any) => {
        setIsLoading(true);
        const { quarter, year } = getQuarterYearObject(event.target.value);
        const quarterInString = getQuarterString(quarter);
        setSelectedQuarter(event.target.value);
        !props.isDashboard && navigate(`/purchase-acceptance/${quarterInString}-${year}`, { replace: true });
        setIsLoading(false);
    };

    const renderSelectPAsQuarter = () => {
        return (
            <div className="selector-container">
                <label className="label" htmlFor="quarter">
                    Select Quarter:
                </label>
                <select
                    className="select"
                    id="quarter"
                    value={selectedQuarter}
                    onChange={(event) => {
                        handleQuarterChange(event);
                    }}
                >
                    <option value="">Select Quarter</option>
                    {dataPAs.map((data: any) => {
                        if (data.yearGRNs.id === selectedYear) {
                            return Object.keys(data.quarterGRNs).map((quarter) => (
                                <option key={quarter} value={quarter}>
                                    {quarter}
                                </option>
                            ));
                        }
                    })}
                </select>
            </div>
        );
    };

    const calculateMetrics = (dataPAsInSelectedQuarter: any[]) => {
        const passed = dataPAsInSelectedQuarter?.reduce((acc: number, data: any) => {
            if (data.status === "PASSED") {
                return acc + 1;
            }
            return acc;
        }, 0);
        const total = dataPAsInSelectedQuarter?.length;
        const KPI = ((passed / total) * 100).toFixed(2);

        return { KPI, passed, total };
    };

    const renderPAsDataTable = () => {
        const dataPAsInSelectedYear = dataPAs.find((data: any) => data.yearGRNs.id === selectedYear);
        const dataPAsInSelectedQuarter = dataPAsInSelectedYear.quarterGRNs[selectedQuarter];
        const { KPI, passed, total } = calculateMetrics(dataPAsInSelectedQuarter);

        return (
            <Table striped bordered hover>
                <thead>
                    <tr>
                        <th>KPI</th>
                        <th>PASSED</th>
                        <th>FAILED</th>
                        <th>TOTAL</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{KPI}%</td>
                        <td>{passed}</td>
                        <td>{total - passed}</td>
                        <td>{total}</td>
                    </tr>
                </tbody>
            </Table>
        );
    };

    const renderPAsFailed = () => {
        const dataPAsInSelectedYear = dataPAs.find((data: any) => data.yearGRNs.id === selectedYear);
        const dataPAsInSelectedQuarter = dataPAsInSelectedYear.quarterGRNs[selectedQuarter];
        const failedPAs = dataPAsInSelectedQuarter?.filter((data: any) => data.status === "FAILED") || [];
        return (
            Boolean(failedPAs.length) && (
                <Container>
                    <p>
                        <b>Failed GRNs</b>
                    </p>
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Title</th>
                                <th>Date of Delivery</th>
                                <th>Date of Approval</th>
                                <th>Difference days</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {failedPAs.map((data: any, index: number) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>
                                        <a target="_blank" href={data.link}>
                                            {data.title}
                                        </a>
                                    </td>
                                    <td>{new Date(data.deliveryDate).toLocaleDateString()}</td>
                                    <td>{new Date(data.effectiveOnDate).toLocaleDateString()}</td>
                                    <td>{getDifferenceDays(data.deliveryDate, data.effectiveOnDate)}</td>
                                    <td>
                                        <button
                                            className="btn btn-primary"
                                            onClick={() => {
                                                updateGRNDocumentStatus(data.id, "PASSED");
                                            }}
                                        >
                                            Mark as Passed
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Container>
            )
        );
    };

    const updateGRNDocumentStatus = async (id: string, status: string) => {
        const document = await updateGRNDocumentStatusAPI(id, status);
        const newData = dataPAs.map((data: any) => {
            if (data.yearGRNs.id === selectedYear) {
                data.quarterGRNs[selectedQuarter] = data.quarterGRNs[selectedQuarter].map((row: any) => {
                    if (row.id === id) {
                        return {
                            ...row,
                            status: document.status,
                            isStatusUpdated: true,
                        };
                    }
                    return row;
                });
            }
            return data;
        });
        setIsUpdateDocumentStatus(true);
        setDataPAs(newData);
    };

    const renderPAPassed = () => {
        const dataPAsInSelectedYear = dataPAs.find((data: any) => data.yearGRNs.id === selectedYear);
        const dataPAsInSelectedQuarter = dataPAsInSelectedYear.quarterGRNs[selectedQuarter];
        const passedPAs = dataPAsInSelectedQuarter?.filter((data: any) => data.status === "PASSED") || [];
        return (
            Boolean(passedPAs.length) && (
                <Container>
                    <p>
                        <b>Passed GRNs</b>
                    </p>
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Title</th>
                                <th>Date of Delivery</th>
                                <th>Date of Approval</th>
                                <th>Difference days</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {passedPAs.map((data: any, index: number) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>
                                        <a target="_blank" href={data.link}>
                                            {data.title}
                                        </a>
                                    </td>
                                    <td>{new Date(data.deliveryDate).toLocaleDateString()}</td>
                                    <td>{new Date(data.effectiveOnDate).toLocaleDateString()}</td>
                                    <td>{getDifferenceDays(data.deliveryDate, data.effectiveOnDate)}</td>
                                    <td>
                                        {data.isStatusUpdated && (
                                            <button
                                                className="btn btn-primary"
                                                onClick={() => {
                                                    updateGRNDocumentStatus(data.id, "FAILED");
                                                }}
                                            >
                                                Mark as Failed
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Container>
            )
        );
    };

    const renderLoading = () => {
        return <MaskLoading></MaskLoading>;
    };
    function extractAllQuarterKeys(data: any[] = dataPAs) {
        const allQuarterKeys: any = [];
        data.forEach((item) => {
            if (item && item.quarterGRNs) {
                const quarterKeys = Object.keys(item.quarterGRNs);
                allQuarterKeys.push(...quarterKeys);
            }
        });
        return allQuarterKeys.sort(sortStringQuarterAndYear);
    }

    const sortStringQuarterAndYear = (a: string, b: string): number => {
        const [quarterA, yearA] = a.split(",").map((part, index) => (index === 0 ? part.slice(-1) : part));
        const [quarterB, yearB] = b.split(",").map((part, index) => (index === 0 ? part.slice(-1) : part));

        if (yearA > yearB) {
            return -1;
        } else if (yearA < yearB) {
            return 1;
        } else {
            return parseInt(quarterB) - parseInt(quarterA);
        }
    };

    const renderTableMultiQuarter = () => {
        return (
            <Table>
                <thead>
                    <tr>
                        <th></th>
                        <th>KPI</th>
                        <th>PASSED</th>
                        <th>FAILED</th>
                        <th>TOTAL</th>
                    </tr>
                </thead>
                <tbody>
                    {dataKPIForMultiQuarter.map((data: any, index: number) => (
                        <tr key={selectedYearAndQuarter[index]}>
                            <td>{selectedYearAndQuarter[index]}</td>
                            <td>{data.KPI}%</td>
                            <td>{data.passed}</td>
                            <td>{data.total - data.passed}</td>
                            <td>{data.total}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        );
    };

    const renderDetailedView = () => {
        return (
            <>
                {renderSelectPAsYear()}
                {isRenderSelectQuarter && renderSelectPAsQuarter()}
                {selectedQuarter && renderPAsDataTable()}
                {selectedQuarter && renderPAsFailed()}
                {selectedQuarter && renderPAPassed()}
            </>
        );
    };

    const renderTableViewMultiQuarter = () => {
        return (
            <>
                <MultiSelector
                    dataOptions={extractAllQuarterKeys()}
                    currentSelected={selectedYearAndQuarter.sort(sortStringQuarterAndYear)}
                    onChange={(data: any[]) => setSelectedYearAndQuarter(data.sort(sortStringQuarterAndYear))}
                    title="Select Year And Quarter"
                ></MultiSelector>
                {selectedYearAndQuarter.length > 0 && renderTableMultiQuarter()}
            </>
        );
    };

    const renderMainContentOfPurchaseAcceptance = () => {
        return (
            <>
                {isLoading && renderLoading()}
                <SwitchViewData currentViewMode={currentViewMode} setCurrentViewMode={(view) => setCurrentViewMode(view)}></SwitchViewData>
                {currentViewMode === VIEW_MODES.DETAILED && !isLoading && renderDetailedView()}
                {currentViewMode === VIEW_MODES.TABLE && !isLoading && renderTableViewMultiQuarter()}
            </>
        );
    };
    return <Container>{renderMainContentOfPurchaseAcceptance()}</Container>;
};

export default PurchaseAcceptance;
